import { ServiceCode, ServiceCodeBySystem, IntegrationSystem } from '@/shared/types';

import { SERVICE_CODES } from '@/lib/consts';

import { RouteConfig, RoutesBySystem } from '../types';

import { getFullRoutePath } from './getFullRoutePath';

type OtherRoutesKeysBySystem = {
  [S in IntegrationSystem]: Exclude<keyof RoutesBySystem<S>, keyof ServiceCodeBySystem<S>>;
};

type OtherRoutesType = { [S in IntegrationSystem]: Record<OtherRoutesKeysBySystem[S], string> };

type RouteProcessingResult = {
  ROUTES_BY_SERVICE_CODE: Record<ServiceCode, string>;
  OTHER_ROUTES: OtherRoutesType;
};

export const createRoutesObject = <T extends Partial<Record<IntegrationSystem, RoutesBySystem<any>>>>(
  routesInput: T,
): RouteProcessingResult => {
  const serviceCodeRoutes: Partial<Record<ServiceCode, string>> = {};
  const otherRoutes: OtherRoutesType = {} as OtherRoutesType;

  for (const [system, routes] of Object.entries(routesInput) as Array<[IntegrationSystem, RoutesBySystem<any>]>) {
    if (!routes) continue;

    const systemOtherRoutes: Record<string, string> = {};

    for (const [routeKey, config] of Object.entries(routes) as Array<[string, RouteConfig]>) {
      const fullPath = getFullRoutePath(system, config);

      if (isServiceCode(routeKey)) {
        serviceCodeRoutes[SERVICE_CODES[routeKey]] = fullPath;
      } else {
        systemOtherRoutes[routeKey] = fullPath;
      }
    }

    if (hasRoutes(systemOtherRoutes)) {
      otherRoutes[system] = systemOtherRoutes as Record<OtherRoutesKeysBySystem[typeof system], string>;
    }
  }

  return {
    ROUTES_BY_SERVICE_CODE: serviceCodeRoutes as Record<ServiceCode, string>,
    OTHER_ROUTES: otherRoutes,
  };
};

const isServiceCode = (key: string): key is keyof typeof SERVICE_CODES => {
  return key in SERVICE_CODES;
};

const hasRoutes = (routes: Record<string, string>): boolean => {
  return Object.keys(routes).length > 0;
};
